/**
 * RecordingFrame — Draggable, resizable frame overlay for selecting
 * the recording area on the canvas.
 *
 * Green border in "frameSelect" phase, red in "recording" phase.
 * Supports drag-to-move and corner-handle resize.
 *
 * When aspectRatio is a fixed preset (e.g. "16:9"), corner-handle
 * resize is locked to that ratio.  "custom" allows free-form resize.
 */

import React, { useCallback, useRef, useState, useEffect } from "react";

import { useAppI18n } from "../hooks/useAppI18n";

import type {
  RecordingPhase,
  FrameRect,
  AspectRatio,
} from "../hooks/useRecordingState";

interface RecordingFrameProps {
  phase: RecordingPhase;
  frame: FrameRect;
  cornerRadius: number;
  aspectRatio: AspectRatio;
  onUpdateFrame: (rect: Partial<FrameRect>) => void;
}

type HandlePosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

const MIN_SIZE = 120;
const GUI_TOP = 72; // Header: 16px offset + 48px height + 8px gap
const EDGE_MARGIN = 24; // Left / right / bottom safety margin (px)

/** Parse an AspectRatio string into a numeric w/h ratio, or null for custom. */
function parseRatio(ar: AspectRatio): number | null {
  switch (ar) {
    case "16:9":
      return 16 / 9;
    case "4:3":
      return 4 / 3;
    case "3:4":
      return 3 / 4;
    case "9:16":
      return 9 / 16;
    case "1:1":
      return 1;
    case "custom":
      return null;
  }
}

export const RecordingFrame: React.FC<RecordingFrameProps> = ({
  phase,
  frame,
  cornerRadius,
  aspectRatio,
  onUpdateFrame,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<HandlePosition | null>(null);
  const dragStart = useRef({ mx: 0, my: 0, fx: 0, fy: 0 });
  const resizeStart = useRef({ mx: 0, my: 0, ...frame });
  const { t } = useAppI18n();

  const isVisible = phase === "frameSelect" || phase === "recording";
  const borderColor = phase === "recording" ? "#ef4444" : "#22c55e";

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      dragStart.current = {
        mx: e.clientX,
        my: e.clientY,
        fx: frame.x,
        fy: frame.y,
      };
    },
    [frame.x, frame.y],
  );

  // ── Resize handlers ───────────────────────────────────────────────────────
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: HandlePosition) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeHandle(handle);
      resizeStart.current = {
        mx: e.clientX,
        my: e.clientY,
        ...frame,
      };
    },
    [frame],
  );

  // ── Global mouse move / up ────────────────────────────────────────────────
  useEffect(() => {
    if (!isDragging && !isResizing) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const dx = e.clientX - dragStart.current.mx;
        const dy = e.clientY - dragStart.current.my;

        // Clamp to all four screen edges
        const rawX = dragStart.current.fx + dx;
        const rawY = dragStart.current.fy + dy;
        onUpdateFrame({
          x: Math.max(
            EDGE_MARGIN,
            Math.min(vw - frame.width - EDGE_MARGIN, rawX),
          ),
          y: Math.max(GUI_TOP, Math.min(vh - frame.height - EDGE_MARGIN, rawY)),
        });
      }

      if (isResizing && resizeHandle) {
        const dx = e.clientX - resizeStart.current.mx;
        const dy = e.clientY - resizeStart.current.my;
        const s = resizeStart.current;

        const ratio = parseRatio(aspectRatio);

        let newX = s.x;
        let newY = s.y;
        let newW = s.width;
        let newH = s.height;

        if (ratio !== null) {
          // ── Locked aspect-ratio resize ─────────────────────────────
          // Project mouse delta onto the diagonal defined by the ratio
          // so resizing feels natural from any corner.
          const signX = resizeHandle.includes("right") ? 1 : -1;
          const signY = resizeHandle.includes("bottom") ? 1 : -1;

          const effDx = signX * dx;
          const effDy = signY * dy;
          const r2 = ratio * ratio;

          // Diagonal projection: how much the user wants to grow/shrink
          const deltaW = (effDx * r2 + effDy * ratio) / (r2 + 1);

          newW = Math.max(MIN_SIZE, s.width + deltaW);
          newH = Math.max(MIN_SIZE, newW / ratio);

          // Re-derive width if height hit the floor
          if (newH <= MIN_SIZE) {
            newH = MIN_SIZE;
            newW = newH * ratio;
          }

          // Adjust origin for handles that grow leftward / upward
          if (signX < 0) {
            newX = s.x + s.width - newW;
          }
          if (signY < 0) {
            newY = s.y + s.height - newH;
            if (newY < GUI_TOP) {
              newY = GUI_TOP;
              newH = s.y + s.height - GUI_TOP;
              newW = newH * ratio;
              if (signX < 0) {
                newX = s.x + s.width - newW;
              }
            }
          }
        } else {
          // ── Free-form resize (custom) ──────────────────────────────
          switch (resizeHandle) {
            case "bottom-right":
              newW = Math.max(MIN_SIZE, s.width + dx);
              newH = Math.max(MIN_SIZE, s.height + dy);
              break;
            case "bottom-left":
              newW = Math.max(MIN_SIZE, s.width - dx);
              newH = Math.max(MIN_SIZE, s.height + dy);
              newX = s.x + (s.width - newW);
              break;
            case "top-right":
              newW = Math.max(MIN_SIZE, s.width + dx);
              newH = Math.max(MIN_SIZE, s.height - dy);
              newY = s.y + (s.height - newH);
              if (newY < GUI_TOP) {
                newH -= GUI_TOP - newY;
                newY = GUI_TOP;
              }
              break;
            case "top-left":
              newW = Math.max(MIN_SIZE, s.width - dx);
              newH = Math.max(MIN_SIZE, s.height - dy);
              newX = s.x + (s.width - newW);
              newY = s.y + (s.height - newH);
              if (newY < GUI_TOP) {
                newH -= GUI_TOP - newY;
                newY = GUI_TOP;
              }
              break;
          }
        }

        // ── Clamp to screen edges ───────────────────────────────────
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Don't let the frame extend past any edge
        if (newX < EDGE_MARGIN) {
          newX = EDGE_MARGIN;
        }
        if (newY < GUI_TOP) {
          newY = GUI_TOP;
        }
        if (newX + newW > vw - EDGE_MARGIN) {
          newW = vw - EDGE_MARGIN - newX;
          if (ratio !== null) {
            newH = newW / ratio;
          }
        }
        if (newY + newH > vh - EDGE_MARGIN) {
          newH = vh - EDGE_MARGIN - newY;
          if (ratio !== null) {
            newW = newH * ratio;
          }
        }

        onUpdateFrame({
          x: newX,
          y: newY,
          width: newW,
          height: newH,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    resizeHandle,
    aspectRatio,
    onUpdateFrame,
    frame.width,
    frame.height,
  ]);

  // Only render in frameSelect or recording phase
  if (!isVisible) {
    return null;
  }

  const handles: HandlePosition[] = [
    "top-left",
    "top-right",
    "bottom-left",
    "bottom-right",
  ];

  const isFrameSelectPhase = phase === "frameSelect";

  return (
    <>
      {/* Dim overlay around frame — visible during both frameSelect and
          recording.  The overlay masks are OUTSIDE the frame so the canvas
          crop pipeline (which crops to inside the frame) will NOT include
          them in the exported video. */}
      {isVisible && (
        <div className="recording-frame-overlay">
          {/* Top */}
          <div
            className="recording-frame-overlay__mask"
            style={{
              top: 0,
              left: 0,
              right: 0,
              height: Math.max(0, frame.y),
            }}
          />
          {/* Bottom */}
          <div
            className="recording-frame-overlay__mask"
            style={{
              top: frame.y + frame.height,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          {/* Left */}
          <div
            className="recording-frame-overlay__mask"
            style={{
              top: frame.y,
              left: 0,
              width: Math.max(0, frame.x),
              height: frame.height,
            }}
          />
          {/* Right */}
          <div
            className="recording-frame-overlay__mask"
            style={{
              top: frame.y,
              left: frame.x + frame.width,
              right: 0,
              height: frame.height,
            }}
          />
        </div>
      )}

      {/* Frame border */}
      <div
        className={`recording-frame ${
          !isFrameSelectPhase ? "recording-frame--recording" : ""
        }`}
        style={{
          left: frame.x,
          top: frame.y,
          width: frame.width,
          height: frame.height,
          borderColor,
          borderRadius: cornerRadius,
          cursor: isFrameSelectPhase
            ? isDragging
              ? "grabbing"
              : "grab"
            : "default",
        }}
        onMouseDown={isFrameSelectPhase ? handleDragStart : undefined}
      >
        {/* Tooltip — only in frameSelect */}
        {isFrameSelectPhase && !isDragging && !isResizing && (
          <div className="recording-frame__tooltip">
            {t("recording.frameTooltip")}
          </div>
        )}

        {/* Corner handles — only in frameSelect */}
        {isFrameSelectPhase &&
          handles.map((pos) => (
            <div
              key={pos}
              className={`recording-frame__handle recording-frame__handle--${pos}`}
              onMouseDown={(e) => handleResizeStart(e, pos)}
              style={{ borderColor }}
            />
          ))}
      </div>
    </>
  );
};
